// Per-conversation message draft persistence. WhatsApp-style: type into
// conversation A, switch to B, switch back to A — the draft is still
// there, including across page reloads. Stored in localStorage so it's
// per-device (drafts intentionally don't sync — they'd cause weird races
// across two open tabs).
//
// Schema: { [conversationId]: { text, updatedAt } } under one key. We
// rewrite the whole blob on every change because drafts are tiny and the
// simplicity wins.

import { useEffect, useState } from 'react';

interface DraftEntry {
  text: string;
  updatedAt: number;
}

type DraftMap = Record<string, DraftEntry>;

const KEY = 'chat:drafts';
const EVENT = 'chat:draft-changed';

// Cap individual drafts to keep localStorage bounded — paste-bombs of
// novel-length text could otherwise explode the quota. 8k matches the
// server's send-message body cap.
const MAX_DRAFT_LEN = 8 * 1024;

// Auto-purge drafts older than this. Stale drafts from conversations the
// user hasn't opened in months bloat the storage.
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function load(): DraftMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as DraftMap;
    if (typeof parsed !== 'object' || parsed === null) return {};
    // Drop expired entries on read so we self-clean without a separate sweep.
    const now = Date.now();
    let mutated = false;
    for (const [k, v] of Object.entries(parsed)) {
      if (!v || typeof v.text !== 'string' || now - (v.updatedAt ?? 0) > TTL_MS) {
        delete parsed[k];
        mutated = true;
      }
    }
    if (mutated) save(parsed);
    return parsed;
  } catch {
    return {};
  }
}

function save(map: DraftMap): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* localStorage full or disabled — best-effort is fine */
  }
}

export function getDraft(conversationId: string): string {
  const map = load();
  return map[conversationId]?.text ?? '';
}

// Returns the set of conversation ids that have a non-empty draft. Used
// by the sidebar to decide which rows render the Draft: prefix.
export function getDraftIndex(): Record<string, string> {
  const map = load();
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(map)) {
    if (v.text) out[k] = v.text;
  }
  return out;
}

export function setDraft(conversationId: string, text: string): void {
  const map = load();
  const trimmed = text.length > MAX_DRAFT_LEN ? text.slice(0, MAX_DRAFT_LEN) : text;
  // Bail out cheaply on no-op writes — happens on initial mount when the
  // component re-persists the value it just loaded. Saves a wasted JSON
  // serialize and a needless re-render of the sidebar's draft index hook.
  const prev = map[conversationId]?.text ?? '';
  if (trimmed === prev) return;
  if (trimmed === '') {
    delete map[conversationId];
  } else {
    map[conversationId] = { text: trimmed, updatedAt: Date.now() };
  }
  save(map);
  // Fire a custom event so the sidebar (and anything else interested) can
  // re-read without prop-drilling. Detail is just the changed convId so
  // listeners can short-circuit if they only care about a specific row.
  window.dispatchEvent(
    new CustomEvent<{ conversationId: string }>(EVENT, {
      detail: { conversationId },
    }),
  );
}

export function clearDraft(conversationId: string): void {
  setDraft(conversationId, '');
}

// React hook: subscribe to the global draft index so a component re-renders
// whenever any draft changes. Cheap — load() is one localStorage read on
// each event. The returned map is keyed by conversationId.
export function useDraftIndex(): Record<string, string> {
  const [index, setIndex] = useState<Record<string, string>>(() => getDraftIndex());
  useEffect(() => {
    const onChange = () => setIndex(getDraftIndex());
    // Cross-tab updates fire the storage event instead of our custom event,
    // so we register both. Same handler — both source the truth from
    // localStorage anyway.
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) onChange();
    };
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return index;
}
